import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./OverlayPlayground.module.scss";

const modalSizes = ["sm", "md", "lg", "xl"] as const;
type ModalSize = (typeof modalSizes)[number];

const modalVariants = ["standard", "plain"] as const;
type ModalVariant = (typeof modalVariants)[number];

const backdropOptions = ["dismissible", "static", "none"] as const;
type BackdropOption = (typeof backdropOptions)[number];

const TALL_CONTENT_ROW_COUNT = 40;
const tallContentRows = Array.from(
    { length: TALL_CONTENT_ROW_COUNT },
    (_, index) => index + 1,
);

const getBackdrop = (option: BackdropOption) => {
    if (option === "none") {
        return false;
    }
    if (option === "static") {
        return "static";
    }
    return true;
};

export const ModalPlayground = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [size, setSize] = useState<ModalSize>("md");
    const [variant, setVariant] = useState<ModalVariant>("standard");
    const [backdropOption, setBackdropOption] =
        useState<BackdropOption>("dismissible");
    const [hasHeader, setHasHeader] = useState(true);
    const [hasFooter, setHasFooter] = useState(true);
    const [isCloseButtonHidden, setIsCloseButtonHidden] = useState(false);
    const [isCentered, setIsCentered] = useState(true);
    const [hasTallContent, setHasTallContent] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const backdrop = getBackdrop(backdropOption);
    const tallContent = hasTallContent ? (
        <ol>
            {tallContentRows.map((row) => (
                <li key={row}>Row {row}</li>
            ))}
        </ol>
    ) : null;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Size</h2>
                        <PlaygroundSelect<ModalSize>
                            options={modalSizes}
                            value={size}
                            onChange={setSize}
                        />
                        <h2 className={styles.subheading}>Variant</h2>
                        <PlaygroundSelect<ModalVariant>
                            options={modalVariants}
                            value={variant}
                            onChange={setVariant}
                        />
                        <h2 className={styles.subheading}>Backdrop</h2>
                        <PlaygroundSelect<BackdropOption>
                            options={backdropOptions}
                            value={backdropOption}
                            onChange={setBackdropOption}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.controls}>
                            <BooleanControl
                                label="Header"
                                checked={hasHeader}
                                onChange={setHasHeader}
                            />
                            <BooleanControl
                                label="Footer"
                                checked={hasFooter}
                                onChange={setHasFooter}
                            />
                            <BooleanControl
                                label="Hide close button"
                                checked={isCloseButtonHidden}
                                onChange={setIsCloseButtonHidden}
                            />
                            <BooleanControl
                                label="Centered"
                                checked={isCentered}
                                onChange={setIsCentered}
                            />
                            <BooleanControl
                                label="Tall content"
                                checked={hasTallContent}
                                onChange={setHasTallContent}
                            />
                            <BooleanControl
                                label="Fullscreen"
                                checked={isFullscreen}
                                onChange={setIsFullscreen}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${variant} · ${size}`}
                output={JSON.stringify(
                    {
                        size,
                        variant,
                        backdrop,
                        hideCloseButton: isCloseButtonHidden,
                        centered: isCentered,
                        fullscreen: isFullscreen,
                        hasTallContent,
                        showcase: { hasHeader, hasFooter, isOpen },
                    },
                    null,
                    2,
                )}
            >
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => setIsOpen(true)}
                >
                    Open modal
                </Button>
                <Modal
                    show={isOpen}
                    onClose={() => setIsOpen(false)}
                    size={size}
                    variant={variant}
                    backdrop={backdrop}
                    hideCloseButton={isCloseButtonHidden}
                    centered={isCentered}
                    fullscreen={isFullscreen}
                >
                    {hasHeader && (
                        <StandardModalHeader>
                            Review transaction
                        </StandardModalHeader>
                    )}
                    {variant === "standard" ? (
                        <StandardModalBody className={styles.overlayBody}>
                            <h3>Confirm the transaction details</h3>
                            <p>
                                This example keeps the body local while showing
                                the production modal structure.
                            </p>
                            {tallContent}
                        </StandardModalBody>
                    ) : (
                        <div className={styles.overlayBody}>
                            <h3>Confirm the transaction details</h3>
                            <p>
                                Plain modals provide their own body wrapper for
                                custom content.
                            </p>
                            {tallContent}
                        </div>
                    )}
                    {hasFooter && (
                        <StandardModalFooter className={styles.modalFooter}>
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={() => setIsOpen(false)}
                            >
                                Save
                            </Button>
                        </StandardModalFooter>
                    )}
                </Modal>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
